import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, MailCheck, XCircle, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function VerifyEmail() {
  const loc = useLocation();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [state, setState] = useState({ status: "checking", message: "" });
  // Guard so the verify-email POST fires exactly once per mount, regardless of
  // AuthContext transitions changing `user`/`refresh`/`nav` identity.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const params = new URLSearchParams(loc.search);
    const token = params.get("token") || "";
    if (!token) { setState({ status: "no_token", message: "No verification token provided." }); return; }
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        setState({ status: "ok", message: data.already_verified ? "Your email was already verified." : "Your email is verified." });
        try { await refresh?.(); } catch (err) { console.debug("refresh after verify-email failed", err); }
        setTimeout(() => { nav(user?.onboarded ? "/dashboard" : "/onboarding", { replace: true }); }, 2000);
      } catch (e) {
        setState({ status: "fail", message: e?.response?.data?.detail || "This link is invalid or has expired." });
      }
    })();
  }, [loc.search, refresh, nav, user?.onboarded]);

  return (
    <div data-testid="verify-email-page" className="min-h-screen flex items-center justify-center px-5 bg-[#F8FAFC]">
      <div className="dms-card p-8 max-w-md w-full text-center">
        {state.status === "checking" && (<>
          <Loader2 className="w-8 h-8 mx-auto text-[#1D4E89] animate-spin" />
          <div className="mt-4 font-heading font-bold text-[#0A2342]">Verifying your email…</div>
        </>)}
        {state.status === "ok" && (<>
          <CheckCircle2 className="w-10 h-10 mx-auto text-[#4FD1C5]" />
          <h1 className="mt-3 font-heading font-bold text-2xl text-[#0A2342]" data-testid="verify-email-ok">You&apos;re verified</h1>
          <p className="mt-2 text-[#475569]">{state.message}</p>
          <p className="mt-2 text-[13px] text-[#94A3B8]">Redirecting…</p>
        </>)}
        {(state.status === "fail" || state.status === "no_token") && (<>
          <XCircle className="w-10 h-10 mx-auto text-[#B91C1C]" />
          <h1 className="mt-3 font-heading font-bold text-2xl text-[#0A2342]" data-testid="verify-email-fail">Verification failed</h1>
          <p className="mt-2 text-[#475569]">{state.message}</p>
          <Link to="/dashboard" className="mt-4 inline-flex dms-btn-primary">Back to dashboard</Link>
        </>)}
      </div>
    </div>
  );
}

export function VerifyEmailBanner() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  if (!user) return null;
  if (user.email_verified) return null;
  if (user.auth_provider === "google") return null;

  const resend = async () => {
    setBusy(true);
    try {
      await api.post("/auth/send-verification-email");
      setSent(true);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="verify-email-banner" className="rounded-md border border-[#FCD34D] bg-[#FEFCE8] px-4 py-3 flex flex-wrap items-center justify-between gap-3">
      <div className="flex items-center gap-2.5 text-[13.5px] text-[#92400E]">
        <MailCheck className="w-4 h-4" />
        <span>Please verify your email so we can activate your trial and unlock generation.</span>
      </div>
      {sent ? (
        <span className="text-[12.5px] text-[#0F766E] font-semibold">Sent - check your inbox</span>
      ) : (
        <button data-testid="resend-verification" onClick={resend} disabled={busy} className="dms-btn-primary text-[12.5px] py-1.5 px-3">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Resend verification email"}
        </button>
      )}
    </div>
  );
}
